import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component"; // Import DataTable
import { CSVLink } from "react-csv"; // Import CSVLink for CSV export
import { baseUrl } from "../../../enum/constant";

const SellAllList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchText, setSearchText] = useState(""); // For search functionality
  const [fromDate, setFromDate] = useState(""); // For 'from' date
  const [toDate, setToDate] = useState(""); // For 'to' date
  const [showPopup, setShowPopup] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [hashTransactionId, setHashTransactionId] = useState("");
  const [uploadingRows, setUploadingRows] = useState({}); // New state to track uploading status

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const base = baseUrl;
      const response = await axios.get(
        base + "api/getAllSellTransactions",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setTransactions(response.data.transactions);
    } catch (err) {
      setError("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newStatus, email) => {
    try {
      const response = await axios.post(
        `${baseUrl}api/StatusTransUpdate/${id}/status`,
        { status: parseInt(newStatus, 10), email },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setTransactions((prevTransactions) =>
        prevTransactions.map((transaction) =>
          transaction.id === id
            ? { ...transaction, status: response.data.data.status }
            : transaction
        )
      );

      setMessage("Transaction status updated successfully");
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError("Failed to update transaction");
    }
  };

  const handleDeleteTransaction = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this transaction?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${baseUrl}api/deleteTransaction/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setTransactions(
        transactions.filter((transaction) => transaction.id !== id)
      );
      setMessage("Transaction deleted successfully");
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError("Failed to delete transaction");
    }
  };

  const handlePopupToggle = (transaction) => {
    setSelectedTransaction(transaction);
    setHashTransactionId(transaction.hash_transaction_id || ""); // Pre-fill with existing hash if available
    setShowPopup(!showPopup);
  };

  const handleUpdateHashTransactionId = async () => {
    const dataToSend = {
      hash_transaction_id: hashTransactionId,
      id: selectedTransaction.id,
      email: selectedTransaction.email,
    };

    try {
      // Send the post request to update the hash_transaction_id
      const response = await axios.post(
        baseUrl + "api/hash-TransictionId",
        dataToSend,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setShowPopup(false); // Close the popup after updating
      setMessage("Hash Transaction ID updated successfully");
      setTimeout(() => setMessage(null), 3000);

      // Optionally, update the transactions state to reflect the updated hash ID in the list
      setTransactions((prevTransactions) =>
        prevTransactions.map((transaction) =>
          transaction.id === selectedTransaction.id
            ? { ...transaction, hash_transaction_id: hashTransactionId }
            : transaction
        )
      );
    } catch (err) {
      setError("Failed to update Hash Transaction ID");
    }
  };

  // New function to handle file change
  const handleFileChange = async (e, transactionId, email) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingRows((prev) => ({ ...prev, [transactionId]: true }));

    const formData = new FormData();
    formData.append("payment_prof", file);
    formData.append("id", transactionId);
    formData.append("email", email);

    try {
      await axios.post(
        baseUrl + "api/upload-payment-prof",
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setMessage("Payment Proof uploaded successfully");
      setTimeout(() => setMessage(null), 3000);

      // Optionally, refresh the transaction list to show the new proof
      fetchTransactions();
    } catch (err) {
      setError("Failed to upload payment proof");
    } finally {
      setUploadingRows((prev) => ({ ...prev, [transactionId]: false }));
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    const searchTextNumber = isNaN(searchText) ? null : Number(searchText);

    const isTextMatch =
      transaction.email.toLowerCase().includes(searchText.toLowerCase()) ||
      (searchTextNumber !== null && transaction.id === searchTextNumber) ||
      transaction.created_at.toString().includes(searchText);

    const transactionDate = new Date(transaction.created_at);
    const isDateInRange =
      (!fromDate || transactionDate >= new Date(fromDate)) &&
      (!toDate || transactionDate <= new Date(toDate));

    return isTextMatch && isDateInRange;
  });

  const columns = [
    {
      name: "Order Id",
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },

    {
      name: "From",
      selector: (row) => {
        if (row.transaction_type === "buy") {
          return `${parseFloat(row.crypto_amount).toFixed(2)} ${row.currencyname}`;
        } else if (row.transaction_type === "sell") {
          return `${parseFloat(row.crypto_amount).toFixed(2)} ${row.criptoname}`;
        }
        return null;
      },
      sortable: true,
    },
    {
      name: "To",
      selector: (row) => {
        if (row.transaction_type === "buy") {
          return `${parseFloat(row.converted_amount)} ${row.criptoname}`;
        } else if (row.transaction_type === "sell") {
          return `${parseFloat(row.converted_amount)} ${row.currencyname}`;
        }
        return null;
      },
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => {
        const date = new Date(row.created_at);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}/${month}/${day}`;
      },
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <select
          className="form-select form-select-sm"
          value={row.status}
          onChange={(e) => handleUpdateStatus(row.id, e.target.value, row.email)}
        >
          <option value={0}>Pending</option>
          <option value={1}>Completed</option>
          <option value={2}>Reviewing</option>
          <option value={3}>Rejected</option>
        </select>
      ),
    },
    {
      name: "Payment Proof ",
      selector: (row) =>
        row.payment_prof ? (
          <a
            href={`${baseUrl}storage/${row.payment_prof}`}
            // href={`${baseUrl}storage/app/public/${row.payment_prof}`}
            target="_blank"
            rel="noopener noreferrer"
            className=""
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              padding: "0",
              textDecoration: "none",
            }}
          >
            <img src="assets/images/check-doc1.png" alt="" />

            {/* <i
              className="bi bi-file-earmark-check"
              style={{ marginRight: "5px", fontSize: "x-large" }}
            ></i> */}
          </a>
        ) : (
          "No Proof"
        ),
    },
    {
      name: "Sell Hash", // New column for Hash Id
      selector: (row) => row.transaction_type === "sell" ? row.hash_transaction_id : "Not Available", // Assuming `hash_id` exists in the data
      sortable: true,
    },
    {
      name: "Proof upload",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <div className="tyuy">
            <input
              type="file"
              name="payment_prof"
              id={`file-input-${row.id}`}
              onChange={(e) => handleFileChange(e, row.id, row.email)}
              accept="image/*, application/pdf"
              style={{ display: "none" }}
            />
            <label
              htmlFor={`file-input-${row.id}`}
              style={{
                display: "inline-block",
                cursor: "pointer",
                padding: "5px 10px",
                backgroundColor: "#4CAF50",
                color: "white",
                borderRadius: "5px",
              }}
            >
              Upload
            </label>
            {uploadingRows[row.id] && <span className="ml-2">Uploading...</span>}
          </div>
        </div>
      ),
    },
    {
      name: "Actions",
      button: true,
      cell: (row) => (
        <button
          className="btn btn-danger btn-sm"
          onClick={() => handleDeleteTransaction(row.id)}
        >
          Delete
        </button>
      ),
    },
  ];

  const exportToCSV = () => {
    return filteredTransactions.map((transaction) => ({
      "Order Id": transaction.id,
      Email: transaction.email,
      Type: transaction.transaction_type.toUpperCase(),
      Crypto: transaction.criptoname,
      Currency: transaction.currencyname,
      From: parseFloat(transaction.crypto_amount).toFixed(2),
      To: parseFloat(transaction.converted_amount).toFixed(2),
      Status:
        transaction.status === 0
          ? "Pending"
          : transaction.status === 1
            ? "Completed"
            : transaction.status === 2
              ? "Reviewing"
              : "Rejected",
    }));
  };

  if (loading) return <div className="mt-md-5 pt-md-5"><div className="mt-md-5"><div className="alert alert-info">Loading...</div></div></div>;
  if (error) return <div className="mt-md-5 pt-md-5"><div className="mt-md-5"><div className="alert alert-danger">Error: {error}</div></div></div>;

  return (
    <div className="cus-dash-container container">
      {message &&
        <div class="cus-alert-msg alert-success" role="alert">
          <div class="icon">
            ✔
            {/* <i class="bi bi-check-lg"></i> */}
          </div>
          <p class="message mb-0">{message}</p>
        </div>
      }
      {/* {message && <div className="alert alert-success">{message}</div>} */}
      <h5 className="mb-4">Buy/Sell Order</h5>

      <div className="d-md-flex justify-content-between mb-3">
        <input
          type="text"
          className="form-control me-3"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="d-md-flex">
          <span className="p-2">From</span>
          <input
            type="date"
            className="form-control me-2"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            placeholder="From Date"
          />
          <span className="p-2">To</span>
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            placeholder="To Date"
          />
        </div>
      </div>

      <CSVLink
        data={exportToCSV()}
        filename={"transactions.csv"}
        className="btn btn-primary mb-2 py-1"
      >
        ⬇
        {/* <i className="bi bi-arrow-down-circle"></i> */}
      </CSVLink>

      <DataTable
        title="Sell Transaction List"
        columns={columns}
        data={filteredTransactions}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 15, 20]}
        highlightOnHover
        fixedHeader
        searchable
      />

      {/* Popup for Hash Transaction ID */}
      {showPopup && selectedTransaction && (
        <div>
          {/* Overlay Background */}
          <div
            className="overlay-background"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999,
            }}
          ></div>

          {/* Modal */}
          <div
            className="modal hash-transh"
            style={{ display: "block", paddingRight: "0px", zIndex: 1000 }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-heade">
                  <h5 className="modal-titl hdt p-3">Transaction Details</h5>
                </div>
                <div className="modal-body">
                  <div>
                    <label
                      htmlFor="hashTransactionId"
                      className=" hdt"
                    >
                      Hash Transaction ID:
                    </label>
                    <input
                      type="text"
                      id="hashTransactionId"
                      value={hashTransactionId}
                      onChange={(e) => setHashTransactionId(e.target.value)}
                      className="form-control"
                      placeholder="Enter Hash Transaction Id"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={handlePopupToggle}>
                    Close
                  </button>
                  <button
                    className="btn btn-primary cus-btn-hover-affect"
                    onClick={handleUpdateHashTransactionId}
                  >
                    Save/Update Hash
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellAllList;


